// @flow
import {
  useEffect, useReducer, useCallback, useState,
} from 'react'

type requestType = (obj?: {
  params: Object,
  requestState: Object,
}) => Promise<any>
type handlerType = (p: Promise<any>) => any

type useRequestParameters = {
  request: requestType,
  handler?: handlerType,
  start?: boolean,
}

type useRequestReturn = {
  state: Object,
  fetch: Function,
  cleanState: Function,
  setData: Function,
}

const actionTypes = {
  LOADING: 'LOADING',
  SUCCESS: 'SUCCESS',
  RESOLVED: 'RESOLVED',
  ERROR: 'ERROR',
  SET_DATA: 'SET_DATA',
  RESET: 'RESET',
}

export function useRequest({ request, handler = null, start = false }: useRequestParameters): useRequestReturn {
  // in a close future this reducer should be extensible with custom configuration, as well as the action dispatched
  // to allow pagination and filtering, and probably auto-refetching too
  const initialState = {
    loading: false,
    error: false,
    success: false,
    data: null,
    pendingHandler: false,
  }

  const [res, dispatch] = useReducer((state, action) => {
    switch (action.type) {
    case actionTypes.LOADING:
      return {
        ...state,
        loading: true,
        error: false,
        success: false,
        data: null,
        params: action.params,
        pendingHandler: !!handler,
      }
    case actionTypes.SUCCESS:
      return {
        ...state,
        params: undefined,
        loading: false,
        data: action.data,
        success: true,
      }
    case actionTypes.RESOLVED:
      return {
        ...state,
        pendingHandler: false,
      }
    case actionTypes.ERROR:
      return {
        ...state,
        params: undefined,
        error: true,
        data: action.error,
        loading: false,
        pendingHandler: false,
      }
    case actionTypes.SET_DATA:
      return {
        ...state,
        data: action.data,
      }
    case actionTypes.RESET:
      return initialState
    default:
      return state
    }
  }, initialState)

  const doRequest = useCallback((params) => {
    dispatch({ type: actionTypes.LOADING, params })
    const result = request.call(null, { params, requestState: res })
    const resultProm = result
      .then((data) => dispatch({ type: actionTypes.SUCCESS, data }))
      .catch((error) => dispatch({ type: actionTypes.ERROR, error }))

    if (handler) {
      return handler(result).then((handlerResult) => {
        dispatch({ type: actionTypes.RESOLVED })

        return handlerResult
      })
    }

    return resultProm
  }, [res, request])

  useEffect(() => {
    if (start) {
      doRequest()
    }
  }, [start])

  const cleanState = useCallback(() => dispatch({ type: actionTypes.RESET }))
  const setData = useCallback(({ data }) => dispatch({ type: actionTypes.SET_DATA, data }))


  return {
    state: res, fetch: doRequest, cleanState, setData,
  }
}

export const useListRequest = ({
  startFilter = {}, negativeFilter = {}, startSearch, startSort,
  startPage = 1, startLimit = 10, request, shouldFilter, ...rest
}) => {
  const [info, setInfo] = useState({
    page: startPage,
    limit: startLimit,
    sort: startSort,
    search: startSearch,
    negativeFilter,
    filter: startFilter,
    shouldRefetch: false,
    shouldFilter,
  })
  const finalRequest = () => request({ params: info })
  const requestState = useRequest({ request: finalRequest, ...rest })

  const setPage = (page) => setInfo({ ...info, page, shouldRefetch: true })
  const setLimit = (limit) => setInfo({ ...info, limit, shouldRefetch: true })
  const setSort = (sort) => setInfo({
    ...info, sort, page: 1, shouldRefetch: true,
  })
  const setFilter = (filter) => setInfo({
    ...info, page: 1, filter: { ...info, ...filter }, shouldRefetch: true,
  })
  const setSearch = (search) => setInfo({
    ...info, page: 1, search, shouldRefetch: true,
  })

  useEffect(() => {
    if (info.shouldRefetch) {
      setInfo({ ...info, shouldRefetch: false })
      requestState.fetch()
    }
  }, [info])

  return {
    ...requestState,
    setPage,
    setLimit,
    setSort,
    setSearch,
    setFilter,
    info,
  }
}
